/**
 * useNavbarUser — derived auth/user display state shared by the desktop
 * profile dropdown and the mobile menu auth section.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

export function useNavbarUser() {
  const navigate = useNavigate();
  const { isAuthenticated, firebaseUser, profile, activeClaim, signOut, loading } = useAuth();

  const displayName = firebaseUser?.displayName || profile?.display_name || 'User';
  const photoURL = firebaseUser?.photoURL || profile?.photo_url || null;
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const email = firebaseUser?.email || profile?.email || '';

  // Approved raagis manage their live profile — no claim CTA, an edit link instead.
  const isRagi = profile?.role === 'raagi';
  // Claim CTA is raagi-intent only (sangat accounts never see claim prompts),
  // and only until the claim is approved.
  const showClaimCta =
    profile?.intended_role === 'raagi' &&
    profile.role !== 'raagi' &&
    profile.role !== 'admin';
  const baseClaimCtaLabel =
    activeClaim?.status === 'draft'
      ? 'Continue claiming profile'
      : activeClaim?.status === 'rejected'
        ? 'Re-claim your profile'
        : 'Claim your Raagi profile';
  // Surface the selected profile name on the CTA when a claim is in progress.
  const claimCtaLabel = activeClaim?.artist_name
    ? `${baseClaimCtaLabel} · ${activeClaim.artist_name}`
    : baseClaimCtaLabel;

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/');
  }, [signOut, navigate]);

  return {
    isAuthenticated,
    loading,
    profile,
    displayName,
    photoURL,
    initials,
    email,
    isRagi,
    showClaimCta,
    claimCtaLabel,
    handleSignOut,
  };
}
